// useUpdateOnFocus.ts
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Platform, AppState } from "react-native";
import UseUpdates from "./UseUpdates";
import Bugsnag from "@bugsnag/expo";

interface UseUpdateOnFocusProps {
  setShowFakeSplashScreen: Dispatch<SetStateAction<Boolean>>;
}

/**
 * Run some routines whilst holding the user at the fake splash screen.
 * This is used to check for updates and load fonts before the app is ready.
 *
 * @param {Function} setShowFakeSplashScreen - Function to control the visibility of the fake splash screen.
 *
 **/
export const useUpdateOnFocus = ({
  setShowFakeSplashScreen,
}: UseUpdateOnFocusProps): { finishedAppSetup: Boolean } => {
  // don't run at all on web...
  if (Platform.OS === "web") return { finishedAppSetup: false };

  // Destructure update functions from UseUpdates hook
  const { isUpdateAvailable, checkForAndApplyUpdateAsync } = UseUpdates();

  // checks for update, downloads and restarts if found
  const checkForUpdate = async () => {
    console.log("[UseFakeSplash] checking for update");
    Bugsnag.leaveBreadcrumb("[UseFakeSplash] checking for update");
    const updateAvailable = await isUpdateAvailable();

    if (updateAvailable) {
      setShowFakeSplashScreen(true);
      Bugsnag.leaveBreadcrumb("[UseFakeSplash] update is available");
      await checkForAndApplyUpdateAsync();
      Bugsnag.leaveBreadcrumb("[UseFakeSplash] update applied");
    } else {
      Bugsnag.leaveBreadcrumb("[UseFakeSplash] update not available");
    }
  };

  // (2) Warm start logic: listen to app state changes and check
  // for updates when the app becomes active.
  useEffect(() => {
    const subscription = AppState.addEventListener(
      "change",
      async (nextAppState) => {
        if (nextAppState === "active") {
          console.log("[UseFakeSplash] ****** Warm start ******");

          await checkForUpdate();
        }
      }
    );

    // Cleanup subscription on unmount
    return () => subscription.remove();
  }, []);
};
